import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Fetch all comments (admin)
export const fetchComments = createAsyncThunk(
  'comment/fetchComments',
  async ({ page = 1, size = 20, search = '' }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page, size });
      if (search) params.append('search', search);
      
      const response = await api.get(`/comments/admin/all?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch comments');
    }
  }
);

// Delete comment
export const deleteComment = createAsyncThunk(
  'comment/deleteComment',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/comments/admin/${id}`);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete comment');
    }
  }
);

// Bulk delete comments
export const bulkDeleteComments = createAsyncThunk(
  'comment/bulkDeleteComments',
  async (commentIds, { rejectWithValue }) => {
    try {
      const response = await api.post('/comments/admin/bulk-moderate', { commentIds });
      return { commentIds, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete comments');
    }
  }
);

// Moderate comment
export const moderateComment = createAsyncThunk(
  'comment/moderateComment',
  async ({ id, action }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/comments/admin/${id}/moderate`, { action });
      return { id, action, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to moderate comment');
    }
  }
);

const commentSlice = createSlice({
  name: 'comment',
  initialState: {
    comments: [],
    loading: false,
    error: null,
    pagination: {
      total: 0,
      page: 1,
      size: 20,
      totalPages: 0
    },
    selectedComments: []
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedComments: (state, action) => {
      state.selectedComments = action.payload;
    },
    toggleCommentSelection: (state, action) => {
      const id = action.payload;
      const index = state.selectedComments.indexOf(id);
      if (index > -1) {
        state.selectedComments.splice(index, 1);
      } else {
        state.selectedComments.push(id);
      }
    },
    clearSelectedComments: (state) => {
      state.selectedComments = [];
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch comments
      .addCase(fetchComments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchComments.fulfilled, (state, action) => {
        state.loading = false;
        state.comments = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchComments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete comment
      .addCase(deleteComment.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        state.loading = false;
        state.comments = state.comments.filter(comment => comment.id !== action.payload.id);
        state.pagination.total -= 1;
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Bulk delete comments
      .addCase(bulkDeleteComments.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkDeleteComments.fulfilled, (state, action) => {
        state.loading = false;
        state.comments = state.comments.filter(
          comment => !action.payload.commentIds.includes(comment.id)
        );
        state.pagination.total -= action.payload.commentIds.length;
        state.selectedComments = [];
      })
      .addCase(bulkDeleteComments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Moderate comment
      .addCase(moderateComment.pending, (state) => {
        state.loading = true;
      })
      .addCase(moderateComment.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.action === 'delete') {
          state.comments = state.comments.filter(comment => comment.id !== action.payload.id);
          state.pagination.total -= 1;
        }
      })
      .addCase(moderateComment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { 
  clearError, 
  setSelectedComments, 
  toggleCommentSelection, 
  clearSelectedComments 
} = commentSlice.actions;

export default commentSlice.reducer;
